import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  IconButton
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { merchantAuthAPI } from '../../../services/merchantAPI';
import { useMerchantAuth } from '../../../contexts/MerchantAuthContext';
import { SuccessPopup } from '../../Common';
import { masterDataConfigAPI } from '../../../services/api';
import FlutterServicesAPI from '../../../services/flutterServices';

const RESEND_COOLDOWN = 30; // seconds

const MerchantOTP = () => {
  const [otp, setOtp] = useState(['', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  // Same cooldown as after resend: fresh OTP was just sent when user lands here.
  const [resendTimer, setResendTimer] = useState(RESEND_COOLDOWN);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [showWhatsAppSmsAuth, setShowWhatsAppSmsAuth] = useState(true);
  const [starterPackage, setStarterPackage] = useState({
    name: 'Starter',
    credits: 1500,
    validity_days: 30,
    description: 'Free starter package with 1500 tokens valid for 30 days'
  });

  const navigate = useNavigate();
  const location = useLocation();
  const { setAuthSession, merchant } = useMerchantAuth();

  const contact = location.state?.contact || '';
  const email = location.state?.email || '';
  const contactType = location.state?.contactType || 'email'; // 'email' | 'phone' | 'dual'
  const countryCode = location.state?.country_code || '+91';

  const formattedPhone = contactType === 'phone' || contactType === 'dual'
    ? (contact.startsWith('+') ? contact : `${countryCode} ${contact}`)
    : '';

  const displayContact = contactType === 'dual'
    ? `${formattedPhone}/ ${email}`
    : (contactType === 'phone' ? formattedPhone : contact);

  const otpRefs = [useRef(null), useRef(null), useRef(null), useRef(null)];

  // Redirect back if no contact was provided
  useEffect(() => {
    if (!contact) {
      navigate('/login');
    }
  }, [contact, navigate]);

  // Cooldown timer for Resend OTP
  useEffect(() => {
    if (resendTimer <= 0) return;
    const t = setTimeout(() => setResendTimer((prev) => prev - 1), 1000);
    return () => clearTimeout(t);
  }, [resendTimer]);

  useEffect(() => {
    const isMobile = window.innerWidth < 600;
    document.body.style.backgroundColor = isMobile ? 'white' : '#f5f5f5';
    return () => {
      document.body.style.backgroundColor = '#f5f5f5';
    };
  }, []);

  useEffect(() => {
    const checkAuthVisibility = async () => {
      try {
        const response = await merchantAuthAPI.checkAuthVisibility();
        if (response.data && typeof response.data.show_whatsapp_sms_auth === 'boolean') {
          setShowWhatsAppSmsAuth(response.data.show_whatsapp_sms_auth);
        }
      } catch (error) {
        console.error('❌ MerchantOTP: Failed to check auth visibility:', error);
      }
    };
    checkAuthVisibility();
  }, []);

  // Fetch starter package information if from registration
  useEffect(() => {
    if (location.state?.fromRegistration) {
      const fetchStarterPackage = async () => {
        try {
          const response = await masterDataConfigAPI.getPublicStarterPackage();
          if (response.data && response.data.starter_package) {
            setStarterPackage(response.data.starter_package);
          }
        } catch (error) {
          console.error('Failed to fetch starter package:', error);
        }
      };
      fetchStarterPackage();
    }
  }, [location.state?.fromRegistration]);

  const handleOtpChange = (index, value) => {
    const cleanValue = value.replace(/\D/g, '');
    if (!cleanValue && value !== '') return;

    // Multi-digit (SMS autofill / paste into first cell): first input has maxLength 4 so the full code can arrive.
    if (cleanValue.length > 1) {
      const pasted = cleanValue.slice(0, 4);
      const newOtp = [...otp];
      pasted.split('').forEach((ch, i) => {
        if (i < 4) newOtp[i] = ch;
      });
      setOtp(newOtp);
      const targetIndex = Math.min(pasted.length, 3);
      otpRefs[targetIndex].current?.focus();
      return;
    }

    const newOtp = [...otp];
    newOtp[index] = cleanValue.slice(-1);
    setOtp(newOtp);

    if (cleanValue !== '' && index < 3) {
      otpRefs[index + 1].current?.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpRefs[index - 1].current?.focus();
    }
  };

  // Paste into first box fills all four cells (desktop / long-press paste).
  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 4);
    if (!pasted) return;
    const newOtp = [...otp];
    pasted.split('').forEach((ch, i) => { newOtp[i] = ch; });
    setOtp(newOtp);
    otpRefs[Math.min(pasted.length, 3)].current?.focus();
  };

  const handleVerifyLogin = async (e) => {
    e.preventDefault();
    const otpCode = otp.join('').replace(/\D/g, '');
    if (otpCode.length < 4) {
      setError('Please enter the complete 4-digit code');
      return;
    }
    setError('');
    setLoading(true);

    try {
      let response;

      // Post-registration: verify email OTP so email_verified is set and role/plan setup is honoured
      if (location.state?.fromRegistration && email) {
        response = await merchantAuthAPI.verifyOTP({ email, otp: otpCode });
      } else if (contactType === 'email') {
        response = await merchantAuthAPI.verifyEmailLoginOTP({ email: contact, otp: otpCode });
      } else {
        // Try WhatsApp verify first, fall back to SMS verify only on network/server errors.
        // Re-throw 4xx so the user sees the actual error (e.g. "Invalid or expired OTP")
        // rather than a misleading "User not found" from the SMS fallback.
        try {
          response = await merchantAuthAPI.verifyWhatsAppOTP({ mobile: contact, otp: otpCode, country_code: countryCode });
        } catch (whatsappErr) {
          if (whatsappErr?.response?.status && whatsappErr.response.status < 500) {
            throw whatsappErr;
          }
          response = await merchantAuthAPI.verifySMSOTP({ mobile: contact, otp: otpCode, country_code: countryCode });
        }
      }

      // Store auth session with token and merchant data
      const token = response?.data?.token || response?.data?.access_token;
      const merchantData = response?.data?.merchant || response?.data?.user;

      if (token && merchantData && setAuthSession) {
        setAuthSession(merchantData, token);
      }

      // Ensure OTP login path also registers device token for mobile push notifications.
      if (FlutterServicesAPI.isFlutterApp()) {
        try {
          await FlutterServicesAPI.getDeviceInfo();
        } catch (deviceError) {
          // Do not block login if device registration fails.
          console.warn('Failed to register device info after OTP login:', deviceError);
        }
      }

      if (location.state?.fromRegistration && showWhatsAppSmsAuth) {
        setShowSuccessPopup(true);
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        'Invalid or expired OTP. Please try again.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    if (resendTimer > 0 || resending) return;
    setResending(true);
    setError('');
    setSuccessMsg('');

    try {
      if (contactType === 'email') {
        await merchantAuthAPI.sendEmailLoginOTP(contact);
      } else if (contactType === 'dual') {
        // For dual delivery, we can use the resend-otp endpoint if we have email,
        // which now sends to both email and whatsapp in the backend.
        if (email) {
          await merchantAuthAPI.resendOTP(email);
        } else {
          try {
            await merchantAuthAPI.sendWhatsAppOTP({ mobile: contact });
          } catch {
            await merchantAuthAPI.sendSMSOTP({ mobile: contact });
          }
        }
      } else {
        // Try WhatsApp first, then SMS
        try {
          await merchantAuthAPI.sendWhatsAppOTP({ mobile: contact });
        } catch {
          await merchantAuthAPI.sendSMSOTP({ mobile: contact });
        }
      }
      setSuccessMsg('OTP resent successfully!');
      setResendTimer(RESEND_COOLDOWN);
      setOtp(['', '', '', '']);
      otpRefs[0].current?.focus();
    } catch (err) {
      const msg = err?.response?.data?.error || err?.response?.data?.message || 'Failed to resend OTP. Please try again.';
      setError(msg);
    } finally {
      setResending(false);
    }
  };

  const handleNavigateToDashboard = () => {
    setShowSuccessPopup(false);
    navigate('/dashboard');
  };

  const handleNavigateToGenerate = () => {
    setShowSuccessPopup(false);
    navigate('/generate');
  };

  // New accounts await admin verification before free tokens — offer plans instead of generating
  const handleNavigateToPlans = () => {
    setShowSuccessPopup(false);
    navigate('/credits');
  };

  const handleCloseSuccessPopup = () => {
    setShowSuccessPopup(false);
    navigate('/dashboard');
  };

  const otpDigits = otp.join('').replace(/\D/g, '');

  return (
    <Container component="main" maxWidth={false}
      sx={{ maxWidth: '560px', mx: 'auto', minHeight: '100vh', display: 'flex', flexDirection: 'column' }} data-testid="merchant-otp-page">
      <Box
        sx={{
          marginTop: { xs: 0.5, sm: 8 },
          marginBottom: { xs: 1, sm: 0 },
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          flexGrow: 1,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            pt: { xs: 2, sm: 4, md: 6 },
            pb: { xs: 3, sm: 5, md: 7 },
            pl: { xs: 2, sm: 4, md: 6 },
            pr: { xs: 2, sm: 3, md: 5 },
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            borderRadius: '18px',
            width: '100%',
            boxShadow: { xs: 'none', sm: 3 },
            border: { xs: 'none', sm: '1px solid #E0E0E0' },
            minHeight: '80vh',
            '@media (min-width: 414px)': {
              pt: 2,
              pb: 2,
              pl: 4,
              pr: 4,
            }
          }}
        >
          {error && (
            <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
              {error}
            </Alert>
          )}

          {successMsg && (
            <Alert severity="success" sx={{ width: '100%', mb: 2 }}>
              {successMsg}
            </Alert>
          )}

          <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 4, mt: 2, position: 'relative' }}>
              <IconButton onClick={() => navigate(-1)} sx={{ position: 'absolute', left: -8, color: '#1A1A1A' }}>
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="h6" className="font-montserret-semibold" sx={{ fontWeight: 600, fontSize: '18px', color: '#1A1A1A', width: '100%', textAlign: 'center' }}>
                Verify Your {contactType === 'dual' ? 'Email/Contact' : (contactType === 'email' ? 'Email' : 'Number')}
              </Typography>
            </Box>

            <Typography variant="body2" className="font-roboto-condensed" sx={{ textAlign: 'center', mb: 2, color: '#27252E', fontSize: '16px', lineHeight: 1.5, px: 2, letterSpacing: '0.1px' }}>
              We just sent a 4-digit code to {displayContact}, enter it below:
            </Typography>

            <Box component="form" onSubmit={handleVerifyLogin} autoComplete="on" sx={{ width: '100%' }}>
              <Typography variant="caption" className="font-roboto-condensed" sx={{ color: '#27252E', mb: 1, display: 'block', ml: 0, fontSize: '16px' }}>
                Code
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: { xs: 1, sm: 2 }, mb: 2 }}>
                {otp.map((digit, index) => (
                  <TextField
                    key={index}
                    inputRef={otpRefs[index]}
                    id={index === 0 ? 'otp' : `otp-${index}`}
                    name={index === 0 ? 'otp' : `otp-${index}`}
                    value={digit}
                    onChange={(e) => handleOtpChange(index, e.target.value)}
                    onKeyDown={(e) => handleOtpKeyDown(index, e)}
                    onPaste={index === 0 ? handlePaste : undefined}
                    autoComplete={index === 0 ? 'one-time-code' : 'off'}
                    inputProps={{
                      maxLength: index === 0 ? 4 : 1,
                      inputMode: 'numeric',
                      autoCorrect: 'off',
                      autoCapitalize: 'off',
                      spellCheck: false,
                      'aria-label': index === 0 ? 'One-time code' : `Digit ${index + 1}`,
                      style: {
                        fontFamily: 'var(--font-roboto) !important',
                        textAlign: 'center',
                        padding: '12px 0px',
                        fontWeight: 400,
                      },
                      sx: {
                        height: { xs: '26px', sm: '35px' },
                        fontSize: { xs: '14px', sm: '18px' }
                      }
                    }}
                    sx={{
                      fontFamily: 'var(--font-roboto) !important',
                      flex: 1,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '10px',
                        '& fieldset': {
                          borderColor: '#9EA1A8',
                        },
                        '&.Mui-focused': {
                          boxShadow: '0 0 0 3px rgba(168, 100, 237, 0.2)',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#A864ED',
                        }
                      }
                    }}
                  />
                ))}
              </Box>

              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{
                  mt: 1,
                  mb: 2,
                  background: 'linear-gradient(90deg, #A864ED 0%, #F4B876 100%)',
                  borderRadius: '10px',
                  textTransform: 'none',
                  fontSize: '16px',
                  fontWeight: 600,
                  py: 1.5,
                  boxShadow: 'none',
                  '&:hover': {
                    background: 'linear-gradient(90deg, #9b5ce0 0%, #e8ad6b 100%)',
                    boxShadow: 'none',
                  }
                }}
                disabled={loading || otpDigits.length < 4}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  'Login'
                )}
              </Button>

              {/* Resend OTP */}
              <Box sx={{ textAlign: 'center', mt: 1 }}>
                {resendTimer > 0 ? (
                  <Typography variant="body2" sx={{ color: '#888', fontSize: '14px' }}>
                    Resend OTP in <strong>{resendTimer}s</strong>
                  </Typography>
                ) : (
                  <Button
                    variant="text"
                    onClick={handleResendOTP}
                    disabled={resending}
                    sx={{ color: '#A864ED', fontWeight: 600, textTransform: 'none', fontSize: '14px' }}
                  >
                    {resending ? <CircularProgress size={16} color="inherit" /> : 'Resend OTP'}
                  </Button>
                )}
              </Box>
            </Box>
          </Box>
        </Paper>
      </Box>

      {/* Footer Text */}
      <Box sx={{ pb: 3, pt: 2, textAlign: 'center', width: '100%', mt: 'auto' }}>
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '12px' }}>
          Transform your business with AI-generated visuals
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          © {new Date().getFullYear()} Glammify. All rights reserved.
        </Typography>
      </Box>
      <SuccessPopup
        open={showSuccessPopup}
        onClose={handleCloseSuccessPopup}
        onNavigateToDashboard={handleNavigateToDashboard}
        onNavigateToGenerate={handleNavigateToGenerate}
        onNavigateToPlans={handleNavigateToPlans}
        verificationStatus={merchant?.verification_status}
        starterPackage={starterPackage}
      />
    </Container>
  );
};

export default MerchantOTP;
